import { useRef } from 'react';
import type React from 'react';

const SLOP_PX = 8;
const MIN_DISTANCE_PX = 96;
const FLICK_MIN_PX = 48;
const WIDTH_SHARE = 0.35;
const FLICK_PX_PER_MS = 0.6;
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

interface Drag {
  readonly pointerId: number;
  readonly startX: number;
  readonly startedAt: number;
  moved: boolean;
  dx: number;
}

export interface SwipeHandlers {
  readonly onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  readonly onClickCapture: (event: React.MouseEvent<HTMLElement>) => void;
}

const reducedMotion = (): boolean => typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION).matches;

const place = (element: HTMLElement, dx: number, snapBack: string | null): void => {
  element.style.transition = snapBack !== null && !reducedMotion() ? snapBack : 'none';
  element.style.transform = dx === 0 ? '' : `translateX(${dx}px)`;
};

export const passedSwipe = (dx: number, width: number, elapsedMs: number): boolean => {
  const distance = Math.abs(dx);
  const threshold = Math.max(width * WIDTH_SHARE, MIN_DISTANCE_PX);
  return distance >= threshold || (distance >= FLICK_MIN_PX && distance / Math.max(elapsedMs, 1) >= FLICK_PX_PER_MS);
};

export const useSwipeDismiss = (onDismiss: () => void, enabled: boolean, snapBack: string): SwipeHandlers => {
  const drag = useRef<Drag | null>(null);
  const swallowClick = useRef(false);

  const end = (element: HTMLElement, pointerId: number, dismiss: boolean): void => {
    const current = drag.current;
    if (current === null || current.pointerId !== pointerId) return;
    drag.current = null;
    swallowClick.current = current.moved;
    element.releasePointerCapture?.(pointerId);
    if (dismiss) {
      onDismiss();
      return;
    }
    place(element, 0, snapBack);
  };

  return {
    onPointerDown: (event) => {
      if (!enabled || event.button > 0) return;
      swallowClick.current = false;
      drag.current = { pointerId: event.pointerId, startX: event.clientX, startedAt: event.timeStamp, moved: false, dx: 0 };
    },
    onPointerMove: (event) => {
      const current = drag.current;
      if (current === null || current.pointerId !== event.pointerId) return;
      current.dx = event.clientX - current.startX;
      if (!current.moved && Math.abs(current.dx) > SLOP_PX) {
        current.moved = true;
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }
      if (current.moved) place(event.currentTarget, current.dx, null);
    },
    onPointerUp: (event) => {
      const current = drag.current;
      if (current === null) return;
      const width = event.currentTarget.getBoundingClientRect().width;
      const dismiss = current.moved && passedSwipe(current.dx, width, event.timeStamp - current.startedAt);
      end(event.currentTarget, event.pointerId, dismiss);
    },
    onPointerCancel: (event) => end(event.currentTarget, event.pointerId, false),
    onClickCapture: (event) => {
      if (!swallowClick.current) return;
      swallowClick.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
  };
};
